import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../components/resultsPrimitives/notificationPrimitives.jsx";
import {
  buildTodayStalePortalRouteKey,
  fetchTodayStalePortalRoutes,
  formatTodayStalePortalRouteLabel,
  publishTodayStalePortalRoute,
} from "../lib/todayStalePortalRoutesApi.js";
import { dispatchPlanningStateChange, subscribePlanningStateChange } from "../utils/planningCrossViewSync.js";

export const TODAY_STALE_PORTAL_ROUTES_POLL_INTERVAL_MS = 10000;
export const TODAY_STALE_PORTAL_SUCCESS_NOTICE_DURATION_MS = 5000;

const EMPTY_TODAY_STALE_PORTAL_ROUTES = Object.freeze({
  dispatchDate: "",
  routes: Object.freeze([]),
});

export default function useTodayStalePortalRoutesAlert({ enabled = false } = {}) {
  const { showToast } = useToast();
  const [state, setState] = useState(EMPTY_TODAY_STALE_PORTAL_ROUTES);
  const [publishingRouteKey, setPublishingRouteKey] = useState("");
  const [successNotices, setSuccessNotices] = useState([]);
  const [dismissedRouteKeys, setDismissedRouteKeys] = useState(() => new Set());
  const enabledRef = useRef(enabled);
  const dispatchDateRef = useRef("");
  const publishingRouteKeyRef = useRef("");
  const successTimersRef = useRef(new Map());
  enabledRef.current = enabled === true;
  dispatchDateRef.current = state.dispatchDate;
  publishingRouteKeyRef.current = publishingRouteKey;

  const refresh = useCallback(async () => {
    if (!enabledRef.current) {
      setState(EMPTY_TODAY_STALE_PORTAL_ROUTES);
      return EMPTY_TODAY_STALE_PORTAL_ROUTES;
    }

    try {
      const payload = await fetchTodayStalePortalRoutes();
      if (!enabledRef.current) {
        setState(EMPTY_TODAY_STALE_PORTAL_ROUTES);
        return EMPTY_TODAY_STALE_PORTAL_ROUTES;
      }
      setState(payload);
      return payload;
    } catch {
      return null;
    }
  }, []);

  const scheduleSuccessNoticeClear = useCallback((routeKey) => {
    if (typeof window === "undefined" || !routeKey) {
      return;
    }
    const previousTimer = successTimersRef.current.get(routeKey);
    if (previousTimer) {
      window.clearTimeout(previousTimer);
    }
    const timeoutId = window.setTimeout(() => {
      setSuccessNotices((current) => current.filter((notice) => notice.key !== routeKey));
      successTimersRef.current.delete(routeKey);
    }, TODAY_STALE_PORTAL_SUCCESS_NOTICE_DURATION_MS);
    successTimersRef.current.set(routeKey, timeoutId);
  }, []);

  const publishRoute = useCallback(async (route) => {
    const routeKey = buildTodayStalePortalRouteKey(route);
    if (!routeKey || publishingRouteKeyRef.current) {
      return null;
    }

    setPublishingRouteKey(routeKey);
    publishingRouteKeyRef.current = routeKey;
    const dispatchDate = route?.dispatchDate || dispatchDateRef.current;
    const routeLabel = formatTodayStalePortalRouteLabel({
      ...route,
      dispatchDate,
    });
    try {
      const result = await publishTodayStalePortalRoute({
        ...route,
        dispatchDate,
      });
      setSuccessNotices((current) => [
        ...current.filter((notice) => notice.key !== routeKey),
        {
          key: routeKey,
          label: routeLabel,
          message: "C'est à nouveau en ligne. Le camionneur voit sa tournée.",
        },
      ]);
      showToast({
        message: `La tournée de ${routeLabel} est de nouveau visible dans le portail chauffeur.`,
        title: "À nouveau en ligne",
        type: "success",
      });
      scheduleSuccessNoticeClear(routeKey);
      dispatchPlanningStateChange("dispatch", {
        actionName: "publish_driver_portal_routes",
        dispatchDate,
        dispatchPeriod: route?.dispatchPeriod,
        portalPublications: result?.payload || null,
        refreshPortalPublications: true,
      });
      await refresh();
      return result;
    } catch (error) {
      showToast({
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de publier cette tournée dans le portail chauffeur.",
        title: "Publication interrompue",
        type: "error",
      });
      throw error;
    } finally {
      publishingRouteKeyRef.current = "";
      setPublishingRouteKey("");
    }
  }, [refresh, scheduleSuccessNoticeClear, showToast]);

  const visibleRoutes = useMemo(
    () => state.routes.filter((route) => !dismissedRouteKeys.has(buildTodayStalePortalRouteKey(route))),
    [dismissedRouteKeys, state.routes],
  );
  const visibleSuccessNotices = useMemo(
    () => successNotices.filter((notice) => !dismissedRouteKeys.has(notice.key)),
    [dismissedRouteKeys, successNotices],
  );

  const dismissAlert = useCallback(() => {
    const routeKeys = state.routes
      .map((route) => buildTodayStalePortalRouteKey(route))
      .filter(Boolean);
    const noticeKeys = successNotices.map((notice) => notice.key).filter(Boolean);

    setDismissedRouteKeys((current) => {
      const next = new Set(current);
      routeKeys.forEach((key) => next.add(key));
      noticeKeys.forEach((key) => next.add(key));
      return next;
    });
  }, [state.routes, successNotices]);

  useEffect(() => {
    const liveKeys = new Set([
      ...state.routes.map((route) => buildTodayStalePortalRouteKey(route)).filter(Boolean),
      ...successNotices.map((notice) => notice.key).filter(Boolean),
    ]);

    setDismissedRouteKeys((current) => {
      if (current.size === 0) {
        return current;
      }

      let changed = false;
      const next = new Set();
      current.forEach((key) => {
        if (liveKeys.has(key)) {
          next.add(key);
        } else {
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [state.routes, successNotices]);

  useEffect(() => {
    if (!enabled) {
      setState(EMPTY_TODAY_STALE_PORTAL_ROUTES);
      setDismissedRouteKeys(new Set());
      return undefined;
    }

    return subscribePlanningStateChange((detail) => {
      const source = typeof detail?.source === "string" ? detail.source.trim().toLowerCase() : "";
      if (source !== "dispatch" && source !== "data-manager") {
        return;
      }
      void refresh();
    });
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return undefined;
    }

    const probe = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      void refresh();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        probe();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    const intervalId = window.setInterval(probe, TODAY_STALE_PORTAL_ROUTES_POLL_INTERVAL_MS);
    probe();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [enabled, refresh]);

  useEffect(() => () => {
    successTimersRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    successTimersRef.current.clear();
  }, []);

  return {
    dismissAlert,
    dispatchDate: state.dispatchDate,
    isPublishing: Boolean(publishingRouteKey),
    publishRoute,
    publishingRouteKey,
    refresh,
    routes: visibleRoutes,
    successNotices: visibleSuccessNotices,
  };
}
